import React, { useState } from 'react';
import { FaTimes, FaChevronUp, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import './MobileBetBar.css';

export default function MobileBetBar({
  bets,
  balance,
  onRemove,
  onUpdateStake,
  onPlace,
  onBetsCleared,
  onNeedAuth,
  onNeedDeposit,
}) {
  const [open, setOpen]           = useState(false);
  const [comboStake, setComboStake] = useState('');
  const [betType, setBetType]     = useState('single');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);

  if (bets.length === 0) return null;

  const totalOdds = bets.reduce((acc, b) => acc * parseFloat(b.odds), 1);

  const totalStake = betType === 'single'
    ? bets.reduce((acc, b) => acc + (parseFloat(b.stake) || 0), 0)
    : parseFloat(comboStake) || 0;

  const potentialWin = betType === 'single'
    ? bets.reduce((acc, b) => acc + (parseFloat(b.stake) || 0) * parseFloat(b.odds), 0)
    : totalStake * totalOdds;

  const applyQuickStake = (amount) => {
    if (betType === 'combo') { setComboStake(String(amount)); return; }
    bets.forEach((b) => onUpdateStake(b.id, String(amount)));
  };

  const handlePlace = async () => {
    setError(null);
    setSuccess(null);

    const items = bets
      .filter((b) => b.fixture_id && b.odd_id)
      .map((b) => ({ fixture_id: b.fixture_id, odd_id: b.odd_id }));

    if (items.length === 0) { setError('Aucun pari valide.'); return; }

    if (betType === 'single') {
      const missing = bets.find((b) => !parseFloat(b.stake));
      if (missing) { setError('Entrez une mise pour chaque pari.'); return; }
    } else {
      if (!parseFloat(comboStake)) { setError('Entrez votre mise.'); return; }
    }

    if (totalStake <= 0) { setError('La mise doit être supérieure à 0.'); return; }

    if (balance < totalStake) {
      setError('Solde insuffisant.');
      setTimeout(() => onNeedDeposit?.(), 1500);
      return;
    }

    setLoading(true);
    try {
      if (betType === 'single') {
        for (const bet of bets) {
          await onPlace({ stake: parseFloat(bet.stake), items: [{ fixture_id: bet.fixture_id, odd_id: bet.odd_id }] });
        }
      } else {
        await onPlace({ stake: parseFloat(comboStake), items });
      }
      setSuccess('Paris placés !');
      setComboStake('');
      setTimeout(() => { setSuccess(null); setOpen(false); onBetsCleared?.(); }, 2000);
    } catch (err) {
      const msg = err.message || '';
      if (msg === 'INSUFFICIENT_BALANCE' || msg.toLowerCase().includes('solde')) {
        setError('Solde insuffisant.');
        setTimeout(() => onNeedDeposit?.(), 1500);
      } else {
        setError(msg || 'Une erreur est survenue.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Overlay ── */}
      {open && <div className="mbb-overlay" onClick={() => setOpen(false)} />}

      {/* ── Drawer ── */}
      <div className={`mbb-drawer ${open ? 'open' : ''}`}>
        {/* Handle */}
        <div className="mbb-handle-wrap" onClick={() => setOpen(!open)}>
          <div className="mbb-handle" />
        </div>

        {/* Header */}
        <div className="mbb-header">
          <div className="mbb-header-left">
            <span className="mbb-count">{bets.length}</span>
            <span className="mbb-title">Ticket de paris</span>
          </div>
          <button className="mbb-close" onClick={() => setOpen(false)}><FaTimes /></button>
        </div>

        {/* Bet type tabs (only when multiple bets) */}
        {bets.length > 1 && (
          <div className="mbb-tabs">
            <button className={`mbb-tab ${betType === 'single' ? 'active' : ''}`} onClick={() => setBetType('single')}>Simples</button>
            <button className={`mbb-tab ${betType === 'combo'  ? 'active' : ''}`} onClick={() => setBetType('combo')}>Combiné</button>
          </div>
        )}

        {/* Bets list */}
        <div className="mbb-bets">
          {bets.map((bet) => (
            <div key={bet.id} className="mbb-bet">
              <div className="mbb-bet-top">
                <div className="mbb-bet-info">
                  <span className="mbb-bet-match">{bet.match}</span>
                  <span className="mbb-bet-sel">{bet.selection}</span>
                </div>
                <div className="mbb-bet-right">
                  <span className="mbb-bet-odd">{parseFloat(bet.odds).toFixed(2)}</span>
                  <button className="mbb-bet-remove" onClick={() => onRemove(bet.id)}><FaTimes size={12} /></button>
                </div>
              </div>
              {betType === 'single' && (
                <div className="mbb-stake-row">
                  <input
                    type="number"
                    className="mbb-stake-input"
                    placeholder="Mise (€)"
                    min="0"
                    value={bet.stake}
                    onChange={(e) => onUpdateStake(bet.id, e.target.value)}
                  />
                  {bet.stake && (
                    <span className="mbb-win-preview">
                      → <strong>{(parseFloat(bet.stake) * parseFloat(bet.odds)).toFixed(2)} FC</strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Combo stake */}
          {betType === 'combo' && (
            <div className="mbb-combo">
              <div className="mbb-combo-odds">
                <span>Cote combinée</span>
                <strong>{totalOdds.toFixed(2)}</strong>
              </div>
              <input
                type="number"
                className="mbb-stake-input"
                placeholder="Mise combinée (€)"
                min="0"
                value={comboStake}
                onChange={(e) => setComboStake(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Quick stakes */}
        <div className="mbb-quick">
          {[10000, 15000, 20000, 50000, 100000].map((a) => (
            <button key={a} className="mbb-quick-btn" onClick={() => applyQuickStake(a)}>
              {a >= 1000 ? `${a / 1000}K` : a} FC
            </button>
          ))}
        </div>

        {/* Summary */}
        <div className="mbb-summary">
          <div className="mbb-summary-row">
            <span>Solde</span>
            <span className={balance < totalStake && totalStake > 0 ? 'warn' : ''}>{Number(balance).toLocaleString('fr-FR')} FC</span>
          </div>
          <div className="mbb-summary-row">
            <span>Mise totale</span>
            <span>{totalStake.toFixed(2)} FC</span>
          </div>
          <div className="mbb-summary-row gain">
            <span>Gain potentiel</span>
            <strong>{potentialWin.toFixed(2)} FC</strong>
          </div>
        </div>

        {error   && <div className="mbb-error"><FaExclamationTriangle size={12} /> {error}</div>}
        {success && <div className="mbb-success"><FaCheckCircle size={12} /> {success}</div>}

        <button
          className={`mbb-place-btn ${loading ? 'loading' : ''}`}
          onClick={handlePlace}
          disabled={loading}
        >
          {loading ? <><span className="mbb-spinner" /> En cours...</> : 'Placer les paris'}
        </button>
      </div>

      {/* ── Collapsed bar (always visible when bets exist) ── */}
      {!open && (
        <button className="mbb-bar" onClick={() => setOpen(true)}>
          <div className="mbb-bar-left">
            <span className="mbb-bar-badge">{bets.length}</span>
            <span className="mbb-bar-label">Voir mon ticket</span>
          </div>
          <div className="mbb-bar-right">
            {potentialWin > 0 && (
              <span className="mbb-bar-win">Gain : <strong>{potentialWin.toFixed(2)} FC</strong></span>
            )}
            <FaChevronUp size={14} />
          </div>
        </button>
      )}
    </>
  );
}
